import { HeurekaElementFactory } from "../util/HeurekaElementFactory";
import tracker from "../tracking/Tracker";
import { listFeatures, readLegacyLabel, submitActionTrackingFrom } from "../tracking/FeatureTracking";
import { filterSheetButton } from "../filtersheet/Selectors";
import { events } from "../events";
import { Filter } from "../filter/Filter";
import { FilterTitle } from "../filter/FilterTitle";
import { isMobile } from "../util/Breakpoint";
import type { TrackingLabels } from "../tracking/TrackingLabels";
import { FilterAccordion } from "../filter/FilterAccordion";

const FILTERLIST_EXPANDER_SELECTOR = "heureka_filterlistExpander";
const FILTERLIST_EXPANDER_EXPANDED_CLASS = `${FILTERLIST_EXPANDER_SELECTOR}--expanded`;
const DESKTOP_EXPANDER_ID = "heureka_filterlistExpanderDesktop";
const MOBILE_EXPANDER_ID = "heureka_filterlistExpanderMobile";

/**
 *
 *
 */
export class FilterlistExpander {
  static readonly factory = HeurekaElementFactory.byClass(FILTERLIST_EXPANDER_SELECTOR, FilterlistExpander);

  constructor(readonly elem: HTMLElement) {}

  /*                                             */
  get isDesktopExpander(): boolean {
    return this.elem.id === DESKTOP_EXPANDER_ID;
  }

  get isMobileExpander(): boolean {
    return this.elem.id === MOBILE_EXPANDER_ID;
  }

  /*                                                    */
  private static desktopExpander(): FilterlistExpander | undefined {
    return FilterlistExpander.factory.byId(DESKTOP_EXPANDER_ID);
  }

  private static mobileExpander(): FilterlistExpander | undefined {
    return FilterlistExpander.factory.byId(MOBILE_EXPANDER_ID);
  }

  /**
 *
 *
 */
  static filterListExpander(): FilterlistExpander | undefined {
    return isMobile() ? FilterlistExpander.mobileExpander() : FilterlistExpander.desktopExpander();
  }

  /*               */
  static register() {
    events.onFilterSectionLoaded(FilterlistExpander.initAll);
  }

  static initAll() {
    FilterlistExpander.factory.forEach((expander) => {
      expander.init();
    });
  }

  private init() {
    this.elem.addEventListener("oc-open", () => {
      /*                                                                                                   */
      FilterlistExpander.factory.forEach((expander) => {
        expander.expanded = true;
        expander.updateFilterTracking(true);
      });
      this.trackSanFoldout(true);
    });
    this.elem.addEventListener("oc-close", () => {
      FilterlistExpander.factory.forEach((expander) => {
        expander.expanded = false;
        expander.updateFilterTracking(false);
      });
      this.trackSanFoldout(false);
    });

    /*                   */
    if (this.expanded) {
      this.updateFilterTracking(true);
    }

    FilterlistExpander.factory.forEach((expander) => {
      expander.updateExpandedListFilterCount();
    });
  }

  /*                      */
  static prepareAll(fragment: DocumentFragment) {
    FilterlistExpander.factory.forEach((expander) => FilterlistExpander.prepare(expander), fragment);
  }

  private static prepare(expander: FilterlistExpander) {
    const oldExpander = FilterlistExpander.filterlistExpanderId(FilterlistExpander.getId(expander));
    if (oldExpander) {
      expander.expanded = oldExpander.expanded;
      if (oldExpander.expanded) {
        expander.elem.setAttribute("expanded", "" + oldExpander.expanded);
      }
    }
  }

  /*                                                         */
  private updateExpandedListFilterCount(): void {
    const expandedListVisibleFilterCount = this.getVisibleFilterCount();
    const expanderTextfield = this.getExpanderShowMoreElement();
    if (expanderTextfield && expandedListVisibleFilterCount > 0) {
      expanderTextfield.setAttribute("data-filter-count", expandedListVisibleFilterCount.toString());
    }
  }

  /**
 *
 *
 */
  private getVisibleFilterCount(): number {
    if (this.isMobileExpander || isMobile()) {
      return FilterTitle.factory.all(this.elem).filter((filterTitle) => !filterTitle?.hidden).length;
    } else {
      return FilterAccordion.factory.all(this.elem).filter((accordion) => !accordion?.hidden).length;
    }
  }

  /*                  */
  private static getId(expander: FilterlistExpander) {
    return expander.elem.id;
  }

  private getExpanderShowMoreElement(): Element | null {
    return this.elem.querySelector(".heureka_expander-text");
  }

  /*                   */
  get expanded(): boolean {
    return this.elem.classList.contains(FILTERLIST_EXPANDER_EXPANDED_CLASS);
  }

  set expanded(state: boolean) {
    if (state) {
      this.elem.setAttribute("expanded", state.toString());
    } else {
      this.elem.removeAttribute("expanded");
    }
    this.elem.classList.toggle(FILTERLIST_EXPANDER_EXPANDED_CLASS, state);
  }

  static filterlistExpanderId(id: string, root?: NonElementParentNode) {
    return FilterlistExpander.factory.byId(id, root);
  }

  /*                   */
  private trackSanFoldout(expandedState: boolean) {
    const san_FilterWhatIf = readLegacyLabel(filterSheetButton(), "san_FilterWhatIf");
    const action: TrackingLabels["san_Foldout"] = expandedState ? "show_more_filters" : "show_less_filters";
    const trackingData: Partial<TrackingLabels> = { san_Foldout: action, san_FilterWhatIf };
    if (expandedState) {
      /*                                                           */
      /*                                                                                  */
      submitActionTrackingFrom(this.elem, "open", trackingData, listFeatures(["Filter-Overview"]));
    } else {
      tracker.submitEvent(trackingData);
    }
  }

  private updateFilterTracking(opened: boolean) {
    Filter.factory.all(this.elem).forEach((filter) => {
      if (filter.featureStatus !== "selected") {
        const statusToUpdate = opened ? "loaded" : "visible";
        const updatedStatus = opened ? "visible" : "loaded";
        filter.updateFeatureStatus(statusToUpdate, updatedStatus);
      }
    });
  }
}
